import { toLonLat, transformExtent } from "ol/proj";
import { setItemWebStorage } from "../../../features/webStorageSaver";
import { createEmpty, extend, getCenter } from "ol/extent";
import GeoJSON from "ol/format/GeoJSON";
import WKT from "ol/format/WKT";
import { store } from "../../../stateManagement/store";
import { setMapPosition, setMapBbox } from "../../../stateManagement/reducers/MapSlice";

// Функция для получения плотности пикселей у пользователя
export const getImageScale = () => {
    const devicePixelRatio = window.devicePixelRatio;
    let dpi = 90 * devicePixelRatio;
    let imageScale = 0;
    if (dpi < 120) {
        imageScale = 1;
        dpi = 90;
    } else if (dpi >= 120 && dpi < 210) {
        imageScale = 2;
        dpi = 180;
    } else if (dpi >= 210) {
        imageScale = 3;
        dpi = 270;
    }
    return { imageScale, dpi };
}

// Функция для преобразования направления в строку для запроса
export function getUrlDirection(reduxDirection: any) {
    const localReduxDirection = parseFloat(reduxDirection)
    if (localReduxDirection === 1) {
        return 'backward'
    } else if (localReduxDirection === 0) {
        return 'forward'
    } else {
        return '0'
    }
}

enum Direction {
    LNG = 'lon',
    LAT = 'lat',
}
// Возвращаем направление
export const getDirectionLocalization = (direction: Direction) => {
    switch (direction) {
        case Direction.LAT: return 'Широта';
        case Direction.LNG: return 'Долгота';
        default: throw new Error('Invalid type');
    }
}

export function formatExtent(localExtent?: any) {
    if (localExtent && localExtent.length === 4) {
        const [first, second, third, fourth] = localExtent;
        if (first > third && second > fourth) {
            return transformExtent([third, fourth, first, second], 'EPSG:4326', 'EPSG:3857');
        } else {
            return transformExtent(localExtent, 'EPSG:4326', 'EPSG:3857');
        }
    } else {
        return undefined;
    }
}

export const cursorStyleFunctions = {
    loading: () => {
        document.body.style.cursor = 'wait';
    },
    grab: () => {
        document.body.style.cursor = 'grab';
    },
    grabbing: () => {
        document.body.style.cursor = 'grabbing';
    },
    notAllowed: () => {
        document.body.style.cursor = 'not-allowed';
    },
    pointer: () => {
        document.body.style.cursor = 'pointer';
    },
    default: () => {
        document.body.style.cursor = 'default';
    }
}

// Преобразование координат в готовый вывод для пользователя
export const shortenDigitsOfCoordIfRendered = (type: Direction, num: number[], mapZoom: number, shouldRender: boolean) => {
    const maximumFractionDigits = mapZoom / 3;
    const minimumFractionDigits = shouldRender ? mapZoom / 3 : 0;
    const getResultString = (num: number | number[] | (string | number)[]) =>
        `${getDirectionLocalization(type)}: ${num.toLocaleString('ru-RU', { minimumFractionDigits, maximumFractionDigits })}`
    if (shouldRender && num) {
        if (type === Direction.LNG) {
            return getResultString(num[0]);
        }
        if (type === Direction.LAT) {
            return getResultString(num[1]);
        }
    } else {
        let [longitude, latitude] = num;
        // let longitudeDMS = decimalToDMS(longitude, false); // Преобразование долготы
        // let latitudeDMS = decimalToDMS(latitude, true); // Преобразование широты
        // if (type === Direction.LNG) {
        //     return (getDirectionLocalization(Direction.LNG) + ': ' + longitudeDMS);
        // }
        // if (type === Direction.LAT) {
        //     return (getDirectionLocalization(Direction.LAT) + ': ' + latitudeDMS);
        // }
    }
}

export function shortenCoordinates(longitude: any, zoom: any) {
    const maximumFractionDigits = zoom / 3;
    const minimumFractionDigits = zoom / 3;
    return longitude.toLocaleString('ru-RU', { minimumFractionDigits, maximumFractionDigits })
}

export function transformLongitudeToDMS(longitude: any) {
    const degrees = Math.floor(longitude);
    const minutesWithFraction = (longitude - degrees) * 60;
    const minutes = Math.floor(minutesWithFraction);
    const seconds = (minutesWithFraction - minutes) * 60;
    const direction = longitude >= 0 ? 'E' : 'W'

    // Заменяем запятые на символы разделителей
    const resultString = [
        Math.abs(degrees) + '°',
        minutes + "'",
        seconds?.toFixed(0) + '"',
        direction
    ];
    return resultString.join(' '); // Объединяем массив в строку с пробелами между элементами
}

export function transformLatitudeToDMS(latitude: any) {
    const degrees = Math.floor(latitude);
    const minutesWithFraction = (latitude - degrees) * 60;
    const minutes = Math.floor(minutesWithFraction);
    const seconds = (minutesWithFraction - minutes) * 60;
    const direction = latitude >= 0 ? 'N' : 'S'

    // Заменяем запятые на символы разделителей
    const resultString = [
        Math.abs(degrees) + '°',
        minutes + "'",
        seconds?.toFixed(0) + '"',
        direction
    ];
    return resultString.join(' '); // Объединяем массив в строку с пробелами между элементами
}

// Дополнительные настройки слоя для динамического обновления выбранного направления
export const configurePanoramaFilters = (directionFilter: any, seasonFilter: any, yearFilter: any) => {
    const layerFilters: string[] = [];
    if (parseFloat(directionFilter) === 1 || parseFloat(directionFilter) === 0) {
        layerFilters.push(`VideoDocumentDirection=${(directionFilter)?.toString()}`);
    }
    if (parseFloat(seasonFilter)) {
        layerFilters.push(`Season=${seasonFilter}`);
    }
    if (parseFloat(yearFilter)) {
        layerFilters.push(`Year=${yearFilter}`);
    }
    const layerFiltersString = layerFilters.join(' AND ');
    if (layerFiltersString) {
        return layerFiltersString;
    } else return null
};

// Считаем требуемый угол для установки
export const calcAngleForDxDy = (dxdy: number[]) => {
    let angle;
    const dx = dxdy[0], dy = dxdy[1];
    if (dy > 0.0001)
        angle = Math.atan(-dx / dy);
    else if (dy < -0.0001)
        angle = Math.PI - Math.atan(dx / dy);
    else if (dx > 0.0001)
        angle = -Math.PI / 2;
    else
        angle = Math.PI * 3 / 4;
    return angle;
}
// Считаем расстояние от курсора до центра маркера панорамы из длин треугольника
export const calcDistanceForDxDy = (dxdy: number[]) => {
    const dx = dxdy[0], dy = dxdy[1];
    return Math.sqrt(dx * dx + dy * dy);
}
// Получение строки для вставки в ссылку запроса с направлением панорамы
export const urlDirection = (reduxDirection: string) => {
    if (reduxDirection === '1') {
        return 'forward'
    } else if (reduxDirection === '0') {
        return 'backward'
    }
    else return '0'
}

// Считаем длины сторон треугольника клика от центра маркера 
export function calcDxDyInPixelsForEventAndFeature(event: any, layer: any, map: any) {
    // Читаем координаты маркера и переводим их в пиксели для расчета длин сторон треугольника
    let coordinate = event.coordinate;
    let featureExtent = layer?.getSource()?.getFeatures()[0].getGeometry().getExtent();
    let featureCenter = getCenter(featureExtent);
    let pxFeatureCenter = map.getPixelFromCoordinate(featureCenter);
    let pxEvtCoordinate = map.getPixelFromCoordinate(coordinate);
    let dx = pxFeatureCenter[0] - pxEvtCoordinate[0];
    let dy = pxFeatureCenter[1] - pxEvtCoordinate[1];
    return [dx, dy];
}

export function localStateReloader(map: any) {
    // Обновляем состояние компонента с новыми координатами и масштабом
    const view = map.getView()
    const resolution: any = view?.getResolution();
    const projection = view?.getProjection().getCode();
    const zoom = view.getZoom()
    const extent = view.calculateExtent()
    const [coordX, coordY] = view.getCenter()
    let [longitude, latitude] = toLonLat([coordX, coordY])
    let boxLonLat = transformExtent(extent, "EPSG:3857", "EPSG:4326");
    let bbox = [boxLonLat[0], boxLonLat[1], boxLonLat[2], boxLonLat[3]]
    let mapPosition = { longitude, latitude, zoom, extent, resolution, projection }
    store.dispatch(setMapPosition(mapPosition))
    setItemWebStorage('mapPosition', mapPosition)
    store.dispatch(setMapBbox(bbox))
}

export function checkGeoJSONGeometryIsNotEmpty(geoJSON: any) {
    if (geoJSON) {
        const featuresGeometry = geoJSON.features.map((feature: any) => feature?.geometry)
        const geometryIsNotEmpty = featuresGeometry.some((geometry: any) => geometry)
        return geometryIsNotEmpty
    } else {
        return false
    }
}

export function countExtentFromGeoJSONFeatures(geoJSON: any, transform?: boolean) {
    // Создание пустой границы
    const initialExtent = createEmpty();
    // Чтение features из GeoJSON
    const features = new GeoJSON().readFeatures(geoJSON);
    // Расширение границы для каждого объекта
    features.forEach((feature) => {
        const geometry = feature.getGeometry();
        if (geometry) {
            extend(initialExtent, geometry.getExtent());
        }
    });
    // Перевод новой границы в систему текущую координат
    if (transform) {
        const extent = transformExtent(initialExtent, 'EPSG:4326', 'EPSG:3857');
        return extent
    }
    else return initialExtent
}

export function countExtentFromWKTFeatures(wkt: any) {
    // Создание пустой границы
    const initialExtent = createEmpty();
    let features: any = []
    // Чтение features из WKT
    if (wkt && Array.isArray(wkt)) {
        wkt.map(wktString => {
            const feature = new WKT().readFeature(wktString);
            features.push(feature)
        })
    }
    if (wkt && !Array.isArray(wkt)) {
        features = new WKT().readFeatures(wkt);
    }
    // Расширение границы для каждого объекта
    features.forEach((feature: any) => {
        const geometry = feature.getGeometry();
        if (geometry) {
            extend(initialExtent, geometry.getExtent());
        }
    });
    // Перевод новой границы в систему текущую координат
    const extent = transformExtent(initialExtent, 'EPSG:4326', 'EPSG:3857');
    return extent
}

// Сортировка диагностики по дате и возвращение объекта с лишь одним актуальным объектом диагностики
export function findMaxEventYearFeature(features: any) {
    if (features) {
        features.sort((a: any, b: any) => parseFloat(b.properties.EventYear) - parseFloat(a.properties.EventYear))
    }
    return features[0]
}
